import { Form, Formik, FormikProps } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import Ripples from 'react-ripples';
import * as yup from 'yup';
import { FavouriteBasketType } from '../../../models/favourite-basket-type';
import { ProductCardLabelsType } from '../../../models/product-card-labels-type';
import { ProductDetailResultType } from '../../../models/product-detail-result-type';
import FormErrorTypeEnum from '../../../models/form-error-type-enum';
import { ProductLineItemType } from '../../../models/product-line-item-type';
import categoryStructure from '../../../services/content-api/endpoints/category/structure';
import favouritesAll from '../../../services/customer-api/endpoints/favourites/all';
import favouritesCreate from '../../../services/customer-api/endpoints/favourites/create';
import favouritesLineAdd from '../../../services/customer-api/endpoints/favourites/line/add';
import ButtonPrimary from '../../atoms/button-primary/button-primary';
import ButtonSecondary from '../../atoms/button-secondary/button-secondary';
import FormText from '../../atoms/form-text/form-text';
import IcomoonIcon from '../../atoms/icomoon-icon/icomoon-icon';
import LoadingSpinner from '../../atoms/loading-spinner/loading-spinner';
import close from '../../providers/overlay/close.overlay.actions';
import open from '../../providers/overlay/open.overlay.actions';
import { OverlayDispatchContext } from '../../providers/overlay/overlay.provider';
import FormCheckboxGroup from '../form-checkbox-group/form-checkbox-group';
import Modal from '../modal/modal';
// eslint-disable-next-line import/no-cycle
import ProductCard from '../product-card/product-card';
import {
  DataLayerContext,
  DataLayerContextType,
} from '../../providers/data-layer/data-layer.provider';
import GtmEventNameEnum from '../../../models/gtm-event-name-enum';
import ProductTypeEnum from '../../../models/product-type-enum';
import getTailwindConfig from '../../../helpers/get-tailwind-config';

interface Props {
  // eslint-disable-next-line prettier/prettier
  readonly basketItem: ProductLineItemType | ProductDetailResultType;
  readonly labels: ProductCardLabelsType;
  readonly onSubmit: () => void;
}

const FavouriteBasketsAddModal: FC<Props> = ({
  basketItem,
  labels,
  onSubmit,
}: Props) => {
  const { pushToDataLayer } =
    useContext<DataLayerContextType>(DataLayerContext);

  const pushProductToDataLayer = (
    event: GtmEventNameEnum,
    errorMessage?: string
  ) =>
    pushToDataLayer(event, {
      featuredProduct: basketItem.product.properties.featuredProduct === '1',
      productType: ProductTypeEnum.Website,
      errorMessage,
    });

  const [baskets, setBaskets] = useState<FavouriteBasketType[]>();
  const [selectedBaskets, setSelectedBaskets] = useState<FavouriteBasketType[]>(
    []
  );
  const [formSuccessfullySubmitted, setFormSuccessfullySubmitted] =
    useState<boolean>(null);
  const [isAddingBasket, setIsAddingBasket] = useState<boolean>(false);
  const [page, setPage] = useState<0 | 1>(0);
  const formRef = useRef<FormikProps<{ baskets: string[] }>>(null);
  const overlayDispatch = useContext(OverlayDispatchContext);
  const fullConfig = getTailwindConfig();
  const frenchBlue = fullConfig.theme.colors['french-blue'];
  const maxHeight = 'max-h-80 lg:max-h-76 xl:max-h-76';
  const maxHeightAdding = 'max-h-40 md:max-h-28 lg:max-h-40';

  const getBaskets = (): void => {
    favouritesAll().then((response: FavouriteBasketType[]) =>
      setBaskets(response)
    );
  };

  const openErrorModal = (basketErrors: FavouriteBasketType[]): void => {
    overlayDispatch(
      open(
        <Modal>
          <h4 className="page-heading-four mb-4">Basket error</h4>

          <p className="text-grey-600 mb-4">
            <span>An error occurred while attempting to add&nbsp;</span>
            <span className="font-bold">{basketItem.product.name}</span>
            <span>
              &nbsp;to the following basket{basketErrors.length > 1 ? 's' : ''}:
            </span>
          </p>

          <ul className="list-disc ml-5">
            {basketErrors.map((basketError: FavouriteBasketType) => (
              <li className="font-bold">{basketError.name}</li>
            ))}
          </ul>

          <p className="text-grey-600 mt-4">
            The item has been added to any other chosen baskets successfully.
          </p>
        </Modal>
      )
    );
  };

  useEffect(() => {
    getBaskets();
  }, []);

  return (
    <Modal>
      <h3 className="block md:hidden page-heading-four w-9/12 mb-6">
        Save to a favourites basket for later
      </h3>
      <div className="flex flex-col md:flex-row space-y-6 md:space-y-0 md:space-x-8 mb-14 md:mb-0 md:-mx-12 lg:mx-0 overflow-y-auto">
        <div className="self-center w-full md:w-5/12">
          <div>
            <ProductCard
              basketItem={basketItem}
              featured={basketItem.product.properties.featuredProduct === '1'}
              labels={labels}
              small
              hideStockAmount
            />
          </div>
        </div>

        <div className="w-full md:w-7/12">
          <h3 className="hidden md:block page-heading-four mb-6 w-9/12 lg:w-7/12">
            Save to a favourites basket for later
          </h3>

          <AnimatePresence onExitComplete={() => setPage(1)}>
            {!isAddingBasket && page === 0 && (
              <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
                <Ripples
                  className="rounded-lg w-full"
                  color={`rgba(${frenchBlue.rgb}, .2)`}
                  during={1200}
                >
                  <button
                    className="bg-light-blue flex items-center focus:outline-none py-3 px-5 rounded-lg w-full"
                    onClick={() => setIsAddingBasket(true)}
                    type="button"
                  >
                    <IcomoonIcon color="french-blue" name="add" size="md" />

                    <span className="ml-4 text-french-blue">
                      Create a new basket
                    </span>
                  </button>
                </Ripples>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence onExitComplete={() => setPage(0)}>
            {isAddingBasket && page === 1 && (
              <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
                <Formik
                  initialValues={{
                    name: '',
                  }}
                  onSubmit={async (values, { setErrors }) => {
                    try {
                      const alreadySelectedBaskets =
                        formRef?.current?.values.baskets;

                      setBaskets(null);

                      const { catalogId } = await categoryStructure();
                      const allBaskets = await favouritesCreate(
                        catalogId,
                        values.name
                      );

                      setSelectedBaskets([
                        ...(alreadySelectedBaskets || []).map(
                          (basketId: string) =>
                            allBaskets.find(
                              (basket: FavouriteBasketType) =>
                                basket.id === basketId
                            )
                        ),
                        allBaskets[allBaskets.length - 1],
                      ]);
                      setBaskets(allBaskets);
                      setIsAddingBasket(false);
                      setFormSuccessfullySubmitted(true);

                      pushProductToDataLayer(GtmEventNameEnum.ProductFavNew);
                    } catch (error) {
                      getBaskets();

                      if (error?.response?.data?.message) {
                        setErrors({
                          name: error.response.data.message,
                        });

                        pushProductToDataLayer(
                          GtmEventNameEnum.ProductFavError,
                          error.response.data.message
                        );

                        return;
                      }

                      setFormSuccessfullySubmitted(false);
                    }
                  }}
                  validateOnMount
                  validateOnChange
                  validationSchema={yup.object({
                    name: yup
                      .string()
                      .required()
                      .trim()
                      .label('Create a new favourite basket'),
                  })}
                >
                  {({ isValid }) => (
                    <Form className="flex flex-col space-y-5">
                      <FormText
                        label="Create a new favourite basket"
                        name="name"
                      />

                      {formSuccessfullySubmitted === false && (
                        <span className="block mt-1 text-sm text-invalid">
                          {FormErrorTypeEnum.Unknown}
                        </span>
                      )}

                      <div className="flex flex-col-reverse lg:flex-row items-center justify-between">
                        <Ripples
                          className="mt-4 lg:mt-0 rounded-full"
                          color={`rgba(${frenchBlue.rgb}, .2)`}
                          during={1200}
                        >
                          <button
                            className="flex items-center focus:outline-none px-4 py-2 rounded-full text-grey-600 text-left"
                            onClick={() => setIsAddingBasket(false)}
                            type="button"
                          >
                            <IcomoonIcon
                              color="french-blue"
                              name="close"
                              size="lg"
                            />

                            <span className="ml-2 text-french-blue">
                              Cancel
                            </span>
                          </button>
                        </Ripples>

                        <ButtonPrimary
                          buttonType="submit"
                          disabled={!isValid}
                          name="Add new favourite basket"
                        />
                      </div>
                    </Form>
                  )}
                </Formik>
              </motion.div>
            )}
          </AnimatePresence>

          {!baskets && <LoadingSpinner className="h-48 md:h-40 lg:h-48" />}

          <AnimatePresence>
            {baskets && !baskets.length && !isAddingBasket && page === 0 && (
              <motion.p
                animate={{ opacity: 1 }}
                className="mt-4 text-grey-600 w-9/12 lg:w-7/12"
                initial={{ opacity: 0 }}
              >
                You haven&apos;t created any favourite baskets yet, you can
                start by clicking the button above.
              </motion.p>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {baskets && baskets.length && (
              <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
                <Formik
                  initialValues={{
                    baskets: selectedBaskets.map(
                      (basket: FavouriteBasketType) => basket.id
                    ),
                  }}
                  innerRef={(values: FormikProps<{ baskets: string[] }>) => {
                    formRef.current = values;
                  }}
                  onSubmit={(values) => {
                    const basketErrors = [];

                    values.baskets.forEach(
                      async (basketId: string, index: number) => {
                        const basket = baskets.find(
                          (selectedBasket: FavouriteBasketType) =>
                            selectedBasket.id === basketId
                        );
                        const isLastItem = values.baskets.length === index + 1;

                        try {
                          await favouritesLineAdd(
                            basketId,
                            basketItem.product.sku,
                            basketItem.product.variantSku
                          );

                          pushProductToDataLayer(
                            GtmEventNameEnum.ProductFavExisting
                          );

                          if (isLastItem) {
                            overlayDispatch(close());
                            onSubmit();
                          }
                        } catch (error) {
                          basketErrors.push(basket);

                          if (isLastItem) {
                            overlayDispatch(close());
                            openErrorModal(basketErrors);
                          }
                        }
                      }
                    );
                  }}
                  validateOnMount
                  validateOnChange
                  validationSchema={yup.object({
                    baskets: yup.array().required(),
                  })}
                >
                  {({ values }) => {
                    const valid = !!values.baskets?.length;

                    return (
                      <Form
                        className={
                          `${isAddingBasket ? maxHeightAdding : maxHeight} ` +
                          `${valid ? 'mb-0' : '-mb-14 md:mb-0'} ` +
                          'custom-scrollbar-primary flex flex-col space-y-5 mt-4 overflow-scroll'
                        }
                      >
                        <FormCheckboxGroup
                          name="baskets"
                          options={baskets
                            .sort(
                              (
                                a: FavouriteBasketType,
                                b: FavouriteBasketType
                              ) => a.name.localeCompare(b.name)
                            )
                            .map((basket: FavouriteBasketType) => ({
                              label: basket.name,
                              value: basket.id,
                            }))}
                        />

                        <AnimatePresence>
                          {valid && (
                            <motion.div
                              animate={{ opacity: 1 }}
                              className="fixed md:absolute bg-white bottom-0 p-2 flex h-24 items-center justify-center left-0 shadow-lg w-full"
                              initial={{ opacity: 0 }}
                            >
                              <ButtonSecondary
                                buttonType="submit"
                                color="turquoise"
                                name="Add to favourite basket and close"
                              />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </Form>
                    );
                  }}
                </Formik>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </Modal>
  );
};

export default FavouriteBasketsAddModal;
